import {
  Box,
  Spacer,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Heading,
  Portal,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePersonalizedPrompts } from '../../../hooks/data/usePersonalizedPrompts';
import { usePersonalizedPromptGeneration } from '../../../hooks/data/usePersonalizedPromptGeneration';
import { useAuth } from '../../../contexts/AuthContext';
import { SuggestedPrompts } from '../../onboarding/SuggestedPrompts';
import { useToast } from '../../../hooks/ui/useToast';
import {
  copySessionAsText,
  deleteAgentSession,
  renameAgentSession,
  stopAgentTurn,
} from '../../../services/agentChat.service';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';

import { useAgentChatSession } from '../../../contexts/AgentChatProvider';
import type { BirdChatHandover } from '../../../services/events/birdChatEvents';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { StatusBanner } from '../../ui/StatusBanner';
import { Tooltip } from '../../ui/Tooltip';
import { PANEL_HEADER_BUTTON, PanelCollapseButton } from '../../layout/PanelHeader';
import {
  BIRD_PANEL_MAX_VIEWPORT_FRACTION,
  BIRD_PANEL_MIN_WIDTH,
  PANEL_DEFAULT_WIDTH,
} from '../../layout/nav/navRailWidth';
import { readPanelWidth } from '../../../hooks/ui/usePanelResize';
import { BirdMessage } from './BirdMessage';
import { deepLinkTargetFrom, useDeepLinkScroll } from './useDeepLinkScroll';
import { useSessionList } from './useSessionList';
import { useLeaveStreamingTurnNotice } from './useLeaveStreamingTurnNotice';
import { SessionHistoryMenu } from './SessionMenuRows';
import { BirdAgentActivity } from './BirdAgentActivity';
import {
  composerTurnState,
  RefusalAnnouncer,
  StoppedWaitingBanner,
  WaitingActivityLine,
} from './BirdChatWaiting';
import { ChatComposer, type ChatComposerPayload, type ChatComposerPrefill } from './ChatComposer';
import { BowerNestLoader } from '../../ui/BowerNestLoader';
import { ChatSessionRating } from '../sessionRating/ChatSessionRating';

interface AgentSession {
  session_id: string;
  user_id: string;
  last_update_time: number;
  title?: string | null;
}

// Refresh page one, KEEPING anything already paged in.
//
// This runs whenever the menu opens and after every answer settles, so a
// plain reset silently threw away every "Load more" the user had clicked —
// the feature had to be re-earned on each menu open, which is most of the
// reason it exists.

/**
 * The current conversation's server-side title, or null while it has none.
 * Null rather than a "Conversation" placeholder: a placeholder beside the Bower
 * wordmark reads as a real name and tells the user nothing.
 */
function titleForSession(sessions: readonly AgentSession[], sessionId: string): string | null {
  const trimmed = sessions.find((s) => s.session_id === sessionId)?.title?.trim();
  return trimmed || null;
}

interface BirdChatPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  onWidthChange?: (width: number) => void;
  onDragStateChange?: (isDragging: boolean) => void;
  /**
   * Drops the header's collapse control. Mobile's full-page host passes it:
   * there is no left column to fold back into there, so the control would
   * promise a gesture that surface cannot perform.
   */
  hideCollapseButton?: boolean;
  /** Override panel height. Defaults to 100dvh (mobile) / 100vh (desktop). */
  panelHeight?: string;
  /** When provided, shows a Live mode button in place of send when input is empty. */
  onLiveTap?: () => void;
  /**
   * A prompt handed off by another surface (Cmd-K, the dashboard hero, an
   * Ask-Bower CTA) to pre-fill or SEND once the panel is open. Applied exactly
   * once per object identity, then consumed via onHandoverConsumed.
   */
  handover?: BirdChatHandover | null;
  /** Opens the command palette pre-scoped to Chats. Omitted on surfaces with no palette. */
  onSearchConversations?: () => void;
  onHandoverConsumed?: () => void;
}

/**
 * What the failed-read banner says, which depends on whether anything is on
 * screen behind it. Either wording is false in the other's case: with rows
 * showing, "couldn't load this conversation" sits directly above the
 * conversation it says is missing.
 *
 * "The rest of" is true for both shapes the non-empty case takes — a transcript
 * that loaded and then failed to refresh, AND a turn the researcher sent before
 * the history arrived, where their own message is the only row and the rest
 * never came. "Refresh" would be wrong for that second one.
 *
 * Out here rather than inline because two ternaries in the JSX put the
 * component over the complexity ceiling, and this is determinism, not layout.
 */
function historyFailureCopy(hasMessages: boolean): { title: string; description: string } {
  if (hasMessages) {
    return {
      title: "Couldn't load the rest of this conversation",
      description: "What's on screen may be incomplete.",
    };
  }
  return {
    title: "Couldn't load this conversation",
    description: 'Something went wrong reading the transcript.',
  };
}

const BirdChatPanel: React.FC<BirdChatPanelProps> = ({
  isOpen,
  onToggle,
  onWidthChange,
  onDragStateChange,
  hideCollapseButton = false,
  panelHeight,
  onLiveTap,
  handover,
  onSearchConversations,
  onHandoverConsumed,
}) => {
  const { user } = useAuth();
  // The SHARED session (AgentChatProvider), never a local `useAgentChat`. This
  // panel is unmounted outright whenever the left-panel slot goes elsewhere —
  // collapsing the chat is exactly that — so a local instance takes the live
  // turn down with it: the message just sent, the assistant placeholder and
  // `isStreaming` all vanish, and reopening re-seeds from a history entry
  // cached before the send. The state has to outlive the view.
  const {
    messages,
    isStreaming,
    isUploadingTurn,
    turnStillWorking,
    refusedDraft,
    clearRefusedDraft,
    isLoadingHistory,
    historyState,
    retryHistory,
    currentAgent,
    sessionId,
    sendMessage,
    cancelMessage,
    respondToConfirmation,
    respondingConfirmationId,
    respondToChoice,
    respondingChoiceId,
    createNewSession,
    switchSession,
  } = useAgentChatSession();

  const {
    sessions,
    totalSessions,
    isLoadingSessions,
    isLoadingMoreSessions,
    hasMoreSessions,
    loadSessions,
    loadMoreSessions,
    renameSession,
    forgetSession,
  } = useSessionList();

  // The header names the conversation, so the list can't wait for the history
  // menu to be opened. Re-runs each time a response settles because the agent
  // titles a session server-side after the first exchange — without that the
  // header would say nothing for the whole of the first conversation.
  useEffect(() => {
    if (!isOpen || isStreaming) return;
    void loadSessions();
  }, [isOpen, isStreaming, loadSessions]);

  const currentSessionTitle = titleForSession(sessions, sessionId);

  const toast = useToast();

  const noteLeavingStreamingTurn = useLeaveStreamingTurnNotice(isStreaming);

  const handleDeleteSession = useCallback(
    async (deleteId: string) => {
      const ok = await deleteAgentSession(deleteId);
      if (!ok) return;
      safeAnalytics.track(ANALYTICS_EVENTS.CHAT_SESSION_DELETED, {} as any);
      forgetSession(deleteId);
      // Stop the server turn, or it keeps spending on a deleted session: through
      // cancelMessage for the reply on screen, else directly (no-op if idle).
      if (deleteId === sessionId && isStreaming) cancelMessage();
      else
        void stopAgentTurn(deleteId).then((delivered) => {
          if (!delivered) console.warn('Chat Stop did not reach the server; the turn runs on');
        });
      if (deleteId === sessionId) createNewSession('session_deleted');
    },
    [sessionId, isStreaming, cancelMessage, createNewSession, forgetSession],
  );

  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const startRenaming = useCallback((sid: string, currentTitle: string) => {
    setRenamingSessionId(sid);
    setRenameValue(currentTitle);
    // Focus input after render
    setTimeout(() => renameInputRef.current?.focus(), 50);
  }, []);

  const cancelRenaming = useCallback(() => {
    setRenamingSessionId(null);
    setRenameValue('');
  }, []);

  const handleRenameSession = useCallback(
    async (sid: string) => {
      const trimmed = renameValue.trim();
      if (!trimmed) {
        cancelRenaming();
        return;
      }
      const ok = await renameAgentSession(sid, trimmed);
      if (ok) {
        renameSession(sid, trimmed);
      }
      cancelRenaming();
    },
    [renameValue, cancelRenaming, renameSession],
  );

  const handleCopySession = useCallback(
    async (copyId: string) => {
      const ok = await copySessionAsText(copyId);
      toast({
        title: ok ? 'Conversation copied' : 'Failed to copy',
        status: ok ? 'success' : 'error',
      });
    },
    [toast],
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const {
    generation: promptGeneration,
    isReady: isPromptRotationReady,
    advance: advancePromptGeneration,
  } = usePersonalizedPromptGeneration('bird_empty', user?.internalId);
  const {
    data: promptPool,
    isError: promptsError,
    isFetching: promptsFetching,
    refetch: refetchPrompts,
  } = usePersonalizedPrompts('bird_empty', [], undefined, isPromptRotationReady, promptGeneration);
  const prompts = promptPool?.prompts ?? [];
  const promptsDegraded = promptPool?.isDegraded === true;

  // Pre-fill the composer when a handover arrives carrying text (e.g. Ask Bower
  // on the equipment page) or when an empty-state prompt chip is tapped. `ts`
  // lets the same text be re-selected.
  const [composerPrefill, setComposerPrefill] = useState<ChatComposerPrefill | null>(null);

  // Resize logic.
  //
  // The drag is DELTA-based: width = width-at-grab + (pointerX - pointerX-at-grab).
  // Deriving width from an absolute viewport coordinate (`e.clientX - sidebarWidth`)
  // was the old approach and it broke as soon as the panel's left edge stopped
  // matching the assumed offset — Layout passes the panel a `sidebarWidth` of 0
  // while actually rendering it beside a 60/260px sidebar, so grabbing the handle
  // snapped the panel a whole sidebar wider (usually straight to max) and left the
  // handle detached from the cursor for the rest of the drag. A delta can't drift:
  // it never needs to know where the panel starts.
  const MIN_WIDTH = useBreakpointValue({ base: 250, md: BIRD_PANEL_MIN_WIDTH }) || 250;
  const MAX_WIDTH_PERCENTAGE =
    useBreakpointValue({ base: 100, sm: 100, md: 100, lg: 50, xl: 50 }) || 50;

  // Seeded through the SHARED reader, against the shared desktop floor rather
  // than the breakpoint `MIN_WIDTH` above: `useBirdPanel` seeds the container
  // from this same call, and the container has no breakpoint hook to consult.
  // (`useBreakpointValue` returns undefined on the first render anyway, so the
  // breakpoint floor was 250 at seed time regardless of viewport.) The resize
  // effect below re-clamps against the live `MIN_WIDTH` on mount, which is what
  // narrows the floor again below md.
  const [width, setWidth] = useState(() =>
    readPanelWidth(
      'birdPanelWidth',
      BIRD_PANEL_MIN_WIDTH,
      window.innerWidth * BIRD_PANEL_MAX_VIEWPORT_FRACTION,
      PANEL_DEFAULT_WIDTH,
    ),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [maxWidth, setMaxWidth] = useState(window.innerWidth * BIRD_PANEL_MAX_VIEWPORT_FRACTION);

  const clampWidth = useCallback(
    (candidate: number) => Math.max(MIN_WIDTH, Math.min(candidate, maxWidth)),
    [MIN_WIDTH, maxWidth],
  );

  useEffect(() => {
    const handleResize = () => {
      const newMax = window.innerWidth * (MAX_WIDTH_PERCENTAGE / 100);
      setMaxWidth(newMax);
      // Re-clamp against MIN_WIDTH too: the breakpoint hook returns undefined on
      // the first render, so a persisted width can land below the desktop floor.
      setWidth((prev) => Math.max(MIN_WIDTH, Math.min(prev, newMax)));
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [MAX_WIDTH_PERCENTAGE, MIN_WIDTH]);

  // Report width to the parent in the same commit the panel resizes in — a
  // deferred (setTimeout) notification left the outer container a tick behind
  // the panel, which reads as tearing while dragging.
  useEffect(() => {
    onWidthChange?.(width);
  }, [width, onWidthChange]);

  // Drag origin, and the width the drag has reached. Refs, not state: the move
  // handler must not be re-created on every frame, and the drag-end write must
  // see the width the last move produced rather than the last committed render.
  const dragOriginRef = useRef<{ pointerX: number; width: number } | null>(null);
  const draggedWidthRef = useRef(width);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragOriginRef.current = { pointerX: e.clientX, width };
      draggedWidthRef.current = width;
      // Pointer capture keeps move/up flowing to this element when the pointer
      // leaves the WINDOW — the document-level mouseup the old version relied on
      // never fires there, so the drag stuck on until the next click. (Embedded
      // content can't steal the stream either way: Layout drops pointer-events
      // on the page while onDragStateChange is true.)
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setIsDragging(true);
      onDragStateChange?.(true);
    },
    [width, onDragStateChange],
  );

  const handleResizeMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const origin = dragOriginRef.current;
      if (!origin) return;
      const next = clampWidth(origin.width + (e.clientX - origin.pointerX));
      draggedWidthRef.current = next;
      setWidth(next);
    },
    [clampWidth],
  );

  const persistWidth = useCallback(() => {
    localStorage.setItem('birdPanelWidth', draggedWidthRef.current.toString());
  }, []);

  // Idempotent, so the handle's own pointerup and the window safety net below
  // can both fire without double-ending.
  const endDrag = useCallback(() => {
    if (!dragOriginRef.current) return;
    dragOriginRef.current = null;
    setIsDragging(false);
    onDragStateChange?.(false);
    persistWidth();
  }, [onDragStateChange, persistWidth]);

  const handleResizeEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragOriginRef.current) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      endDrag();
    },
    [endDrag],
  );

  // Keyboard resize — the handle is focusable, so it needs to be operable
  // without a pointer (and it's the accessible escape hatch from a trackpad).
  const handleResizeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const step = e.shiftKey ? 64 : 16;
      // Computed outside the updater: a state updater must stay pure (StrictMode
      // double-invokes it), and persisting is a side effect.
      const next = clampWidth(width + (e.key === 'ArrowLeft' ? -step : step));
      draggedWidthRef.current = next;
      setWidth(next);
      // Persisted on key UP, not here: auto-repeat on a held arrow fires ~30
      // keydowns a second, and a synchronous localStorage write per event is
      // what the drag path was moved off.
    },
    [clampWidth, width],
  );

  useEffect(() => {
    if (!isDragging) return;
    // Safety net for the handle unmounting mid-drag. `showResizeHandle` is md+,
    // but the PANEL only unmounts on a touch device (`isMobile` requires
    // touch/iOS/Android), so narrowing a desktop window past 768px — or ⌘/Ctrl
    // +/- zoom, which needs no pointer — removes the handle while the drag is
    // live. Its own pointerup then never arrives, and Layout would hold
    // pointer-events:none over the whole page: an app that ignores every click
    // with nothing on screen to explain it. The document-level listener this
    // PR replaced happened to cover this; capture alone does not.
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, endDrag]);

  const { highlightedMessageId, isDeepLinkInFlight, armDeepLink, releaseDeepLink } =
    useDeepLinkScroll(handover, messages, messagesContainerRef);

  // Auto-scroll to the newest message.
  //
  // Guarded by a REF, not by the deep-link state: reading the state here made
  // clearing it re-run this effect, which then smooth-scrolled to the bottom
  // and undid the jump one commit after it landed.
  useEffect(() => {
    if (isDeepLinkInFlight()) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming, isDeepLinkInFlight]);

  // A message the server refused (a turn was still running) goes back into the
  // composer ONCE: moved into this panel's own prefill state, which is a stable
  // object the composer applies a single time, and cleared at the source in
  // the same pass. Passed straight through instead, it was a fresh object on
  // every render, and the composer re-applied it over whatever the researcher
  // had typed since, on every 4 s poll. Chips stay: a send never clears them.
  useEffect(() => {
    if (!refusedDraft) return;
    setComposerPrefill({
      text: refusedDraft.text,
      files: refusedDraft.files,
      ts: refusedDraft.ts,
      keepContexts: true,
    });
    clearRefusedDraft();
  }, [refusedDraft, clearRefusedDraft]);

  // Composer submit — the ONE place a message actually enters the chat.
  const handleComposerSubmit = useCallback(
    (payload: ChatComposerPayload) => {
      void sendMessage(payload.text, payload.contexts, payload.files);
      // Signal onboarding checklist that the user actually sent a Bower message
      window.dispatchEvent(new CustomEvent('onboarding:birdMessageSent'));
    },
    [sendMessage],
  );

  // ── Handover from another surface ────────────────────────────────
  //
  // Applied in two phases, deliberately across renders:
  //
  //  1. Pre-fill, and — when the handover asks for fresh context — start a new
  //     session. `createNewSession` only SCHEDULES the id swap.
  //  2. Send, but only once `sessionId` is actually the new one. `sendMessage`
  //     closes over `sessionId`, so sending in phase 1 would post the prompt to
  //     the conversation we just navigated away from — the exact bug the
  //     fresh-context default exists to prevent.
  //
  // Both phases key off the handover's object identity so each runs exactly once
  // for a given handover, whatever the parent does with the prop afterwards.
  // That independence matters: the reset is destructive to the visible thread,
  // and a duplicate send would post the user's message twice.
  //
  // Phase 1 does NOT wait for an in-flight response: `createNewSession` aborts
  // the stream, which is the honest reading of "start a new conversation about
  // this". The abandoned turn is still persisted server-side, so it stays
  // reachable from the history menu.
  const appliedHandoverRef = useRef<BirdChatHandover | null>(null);
  const sentHandoverRef = useRef<BirdChatHandover | null>(null);
  const sessionAwaitingResetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !handover || appliedHandoverRef.current === handover) return;
    appliedHandoverRef.current = handover;
    // `send` takes precedence over a simultaneous `initialMessage` (see
    // birdChatEvents.ts) — pre-filling too would strand text in the composer
    // looking unsent after the payload already went out.
    if (handover.initialMessage && !handover.send) {
      setComposerPrefill({
        text: handover.initialMessage,
        ts: Date.now(),
        contexts: handover.initialContexts,
      });
    }
    if (handover.sessionId) {
      // Chat-history search matched a specific thread. Arm the scroll BEFORE
      // switching so the suppression is already in place when the new history
      // lands.
      const target = deepLinkTargetFrom(handover);
      if (target) armDeepLink(target);
      if (handover.sessionId !== sessionId) switchSession(handover.sessionId);
      sessionAwaitingResetRef.current = null;
    } else if (handover.freshContext) {
      sessionAwaitingResetRef.current = sessionId;
      createNewSession('handover');
    } else {
      // A resume handover must also RESET the guard: a previous prefill-only
      // fresh handover leaves the pre-reset id parked here (nothing else
      // clears it), and if the user had switched back to that very session,
      // phase 2 would wait on it forever and silently drop the send.
      sessionAwaitingResetRef.current = null;
    }
    // A pre-fill-only handover is finished here. One carrying `send` is finished
    // in the effect below, once its message has actually gone out.
    if (!handover.send) onHandoverConsumed?.();
  }, [
    isOpen,
    handover,
    sessionId,
    createNewSession,
    switchSession,
    onHandoverConsumed,
    armDeepLink,
    releaseDeepLink,
  ]);

  // Phase 2. `sendMessage` silently no-ops while streaming / before the
  // workspace is ready, so gate on readiness and only consume the handover when
  // the message has actually been dispatched — otherwise it would vanish.
  useEffect(() => {
    if (!isOpen || !handover?.send) return;
    if (appliedHandoverRef.current !== handover) return;
    if (sentHandoverRef.current === handover) return;
    if (sessionAwaitingResetRef.current !== null) {
      // The new id hasn't landed yet; this same effect re-runs when it does.
      if (sessionAwaitingResetRef.current === sessionId) return;
      sessionAwaitingResetRef.current = null;
    }
    if (isStreaming || isLoadingHistory) return;
    sentHandoverRef.current = handover;
    handleComposerSubmit(handover.send);
    onHandoverConsumed?.();
  }, [
    isOpen,
    handover,
    sessionId,
    isStreaming,
    isLoadingHistory,
    handleComposerSubmit,
    onHandoverConsumed,
  ]);

  const showResizeHandle = useBreakpointValue({ base: false, md: true }) || false;
  const lastMessage = messages[messages.length - 1];

  // The panel fills whatever container it's placed in rather than sizing itself
  // to `width`. `width` is still the number being dragged — it's reported to the
  // container via onWidthChange — but only ONE element applies it. When both did,
  // the container picked the new width a render later than the panel, so a fast
  // drag left the panel briefly overhanging the page content.
  const openWidth = '100%';

  const sessionMenuContent = (
    <SessionHistoryMenu
      sessions={sessions}
      currentSessionId={sessionId}
      renamingSessionId={renamingSessionId}
      renameValue={renameValue}
      renameInputRef={renameInputRef}
      onRenameValueChange={setRenameValue}
      onRenameSubmit={(id) => void handleRenameSession(id)}
      onRenameCancel={cancelRenaming}
      onRenameStart={startRenaming}
      onSelect={(id) => {
        // The user chose a different conversation, so any pending jump is
        // abandoned — otherwise the suppression follows them into it and it
        // opens scrolled to the top of its transcript.
        releaseDeepLink();
        // Picking the open conversation is a no-op in `switchSession`, so
        // nothing is left behind and there is nothing to say.
        if (id !== sessionId) noteLeavingStreamingTurn('history');
        switchSession(id);
      }}
      onCopy={(id) => void handleCopySession(id)}
      onDelete={(id) => void handleDeleteSession(id)}
      isLoadingSessions={isLoadingSessions}
      isLoadingMoreSessions={isLoadingMoreSessions}
      hasMoreSessions={hasMoreSessions}
      totalSessions={totalSessions}
      onSearchConversations={onSearchConversations}
      onLoadMore={() => void loadMoreSessions()}
    />
  );

  const getMessagesContent = () => {
    if (isLoadingHistory) {
      return (
        <Flex justify="center" align="center" h="full">
          <BowerNestLoader size="md" />
        </Flex>
      );
    }
    // `forbidden` ONLY. This branch runs BEFORE the `messages.length` check
    // below, so anything routed through it REPLACES the transcript — which is
    // right for "not yours to read" (there is nothing to show and nothing to
    // retry) and catastrophic for a transient read failure, where it would
    // delete the researcher's own just-sent message and the streaming row in
    // favour of a string. That failure is a banner above this content instead.
    if (historyState.kind === 'forbidden') {
      return (
        <Flex justify="center" align="center" h="full">
          <Text fontSize="sm" color="text.muted">
            {historyState.message}
          </Text>
        </Flex>
      );
    }
    // A failed read is not an empty conversation, so it must not render the
    // empty state: "Start a conversation with Bower" plus suggested prompts,
    // over a conversation that exists server-side, is the false statement this
    // whole change exists to stop. The banner carries the pane on its own.
    if (messages.length === 0 && historyState.kind === 'load_failed') {
      return null;
    }
    if (messages.length === 0) {
      return (
        <Flex justify="center" align="center" h="full" px={4}>
          <VStack spacing={4} maxW="360px" w="full">
            <Text fontSize="sm" color="text.muted" textAlign="center">
              Start a conversation with Bower
            </Text>
            <SuggestedPrompts
              prompts={prompts}
              source="chat_empty_state"
              isError={promptsError || promptsDegraded}
              isRetrying={promptsFetching}
              onRetry={() => void refetchPrompts()}
              onSelect={(prompt) => {
                setComposerPrefill({
                  text: prompt.text,
                  ts: Date.now(),
                  contexts: prompt.context ? [prompt.context] : [],
                });
                advancePromptGeneration();
              }}
            />
          </VStack>
        </Flex>
      );
    }
    return (
      <>
        {messages.map((msg) => (
          <Box
            key={msg.id}
            data-message-id={msg.id}
            data-highlighted={msg.id === highlightedMessageId ? 'true' : 'false'}
            borderRadius="md"
            transition="background-color 600ms ease-out"
            bg={msg.id === highlightedMessageId ? 'bg.muted' : 'transparent'}
          >
            <BirdMessage
              message={msg}
              onRespondToConfirmation={handleRespondToConfirmation}
              respondingConfirmationId={respondingConfirmationId}
              onRespondToChoice={handleRespondToChoice}
              respondingChoiceId={respondingChoiceId}
            />
          </Box>
        ))}
        <ChatSessionRating {...{ sessionId, lastMessage, isStreaming }} />
        {isStreaming && lastMessage?.role === 'assistant' && (
          <BirdAgentActivity
            activities={lastMessage.activities ?? []}
            currentAgent={currentAgent}
          />
        )}
        <WaitingActivityLine turnStillWorking={turnStillWorking} />
      </>
    );
  };
  // `void` rather than await: the resumed turn streams into the transcript,
  // so there is nothing for the click handler to wait on.
  const handleRespondToConfirmation = useCallback(
    (callId: string, confirmed: boolean) => {
      // `void` on the promise: the resumed turn streams into the transcript,
      // so there is nothing for the click handler to await. But a DECLINED
      // reply (another turn still streaming) has to be said out loud — the
      // researcher pressed Confirm on a destructive action and the dialog is
      // still sitting there.
      void respondToConfirmation(callId, confirmed).then((sent) => {
        if (sent) return;
        toast({
          title: 'Still working on your last message',
          description: 'Wait for it to finish, then confirm again.',
          status: 'warning',
        });
      });
    },
    [respondToConfirmation, toast],
  );

  const handleRetryHistory = useCallback(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_HISTORY_RETRIED, { session_id: sessionId });
    retryHistory();
  }, [retryHistory, sessionId]);

  const handleRespondToChoice = useCallback(
    (callId: string, value: string) => {
      // Same shape as the confirmation handler, and declined for the same
      // reason: another turn is still streaming. Less alarming to say than a
      // declined confirmation — nothing destructive is pending — but still
      // said, because the researcher clicked and the control is still there.
      void respondToChoice(callId, value).then((sent) => {
        if (sent) return;
        toast({
          title: 'Still working on your last message',
          description: 'Wait for it to finish, then pick again.',
          status: 'warning',
        });
      });
    },
    [respondToChoice, toast],
  );

  const messagesContent = getMessagesContent();

  return (
    <Box
      position="relative"
      h={panelHeight ?? { base: '100dvh', md: '100vh' }}
      w={isOpen ? openWidth : '48px'}
      transition={isDragging ? 'none' : 'width 0.2s ease'}
      // `frame.panel`, the SAME white the Home canvas is drawn on — not
      // `bg.surface`, which the 2026-09 frame flip turned into parchment. The
      // chat panel is a canvas you read long-form text on, the same kind of
      // surface as the page beside it, so it takes the page's fill rather than
      // the "content box drawn ON the page" fill.
      bg="frame.panel"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Resize Handle — 12px grab target, 3px visual. A 4px target is a
          coin-flip to hit with a trackpad, which is most of why resizing felt
          unreliable; the parent clips overflow, so the slop goes inwards.
          Gated on `onWidthChange`: the panel fills its container rather than
          sizing itself, so without a parent applying the width the handle would
          be a focusable, value-announcing separator that moves nothing — which
          is what /mobile/bird (no onWidthChange) renders at md+ on a tablet or
          a landscape phone. */}
      {isOpen && showResizeHandle && !!onWidthChange && (
        <Box
          position="absolute"
          right="0"
          top="0"
          bottom="0"
          w="12px"
          cursor="col-resize"
          sx={{ touchAction: 'none' }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat panel"
          // A focusable separator is a window splitter — without these, screen
          // readers announce a made-up "50".
          aria-valuenow={Math.round(width)}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={Math.round(maxWidth)}
          tabIndex={0}
          onPointerDown={handleResizeStart}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
          onPointerCancel={handleResizeEnd}
          onKeyDown={handleResizeKeyDown}
          onKeyUp={persistWidth}
          onBlur={persistWidth}
          zIndex="docked"
          _after={{
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            w: '3px',
            bg: isDragging ? 'brand.primary' : 'transparent',
            transition: 'background-color 0.2s ease',
          }}
          _hover={{ _after: { bg: 'brand.primary' } }}
          _focusVisible={{ outline: 'none', _after: { bg: 'brand.primary' } }}
        />
      )}

      {/* Header */}
      <Flex
        data-chat-header
        align="center"
        px={isOpen ? '18px' : 2}
        py={isOpen ? '16px' : 3}
        borderBottom="1px"
        borderColor="border.subtle"
        bg="frame.panel"
        flexShrink={0}
      >
        {isOpen ? (
          <HStack spacing={2.5} w="full" minW={0}>
            {/* Same display treatment as the dashboard home title, so the panel
                reads as the same brand moment as the home surface rather than
                as a plain chrome label.

                "Bower", not "Chat": the rail item beside it is already called
                Chat, so repeating the function here says nothing, and the one
                place the product names itself should name the product. */}
            <Heading
              as="h2"
              variant="display"
              fontSize="17px"
              lineHeight="22px"
              color="text.primary"
              flexShrink={0}
            >
              Bower
            </Heading>
            {/* Takes the slack, so a long conversation title truncates instead
                of pushing the buttons off the panel. Absent until the session
                has been titled server-side — the header stays bare rather than
                inventing a placeholder. */}
            {currentSessionTitle ? (
              <Text
                data-chat-session-title
                fontSize="13px"
                lineHeight="17px"
                color="text.muted"
                noOfLines={1}
                flex={1}
                minW={0}
              >
                {currentSessionTitle}
              </Text>
            ) : (
              <Spacer />
            )}
            {/* Cached list mid-stream; the effect above reloads once it settles.
                Mounted mid-reply there is no cache, so load it anyway. */}
            <Menu
              onOpen={() => {
                if (!isStreaming || sessions.length === 0) void loadSessions();
              }}
            >
              <Tooltip label="Previous conversations">
                <MenuButton
                  as={IconButton}
                  aria-label="Previous conversations"
                  icon={<MaterialIcon name="history" boxSize={4} />}
                  {...PANEL_HEADER_BUTTON}
                  // Not yet POSTed while files upload: leaving would drop it.
                  isDisabled={isUploadingTurn}
                />
              </Tooltip>
              {/* Portalled, like every other menu in the left chrome
                  (HelpMenu, AccountMenu, CreateMenu). This panel's root sets
                  `overflow="hidden"` — deliberately, so the resize handle's
                  grab slop falls inwards — and an inline MenuList is clipped by
                  it, so the conversation list was cut off at the panel edge.
                  `zIndex="toast"` because the panel column raises itself to
                  `modal` when it overlays on a narrow viewport. */}
              <Portal>
                <MenuList maxH="300px" overflowY="auto" zIndex="toast">
                  {sessionMenuContent}
                </MenuList>
              </Portal>
            </Menu>
            <Tooltip label="New conversation">
              <IconButton
                aria-label="New conversation"
                icon={<MaterialIcon name="edit_square" boxSize={4} />}
                onClick={() => {
                  // Same reasoning as picking another conversation: starting a
                  // fresh one abandons any pending jump.
                  releaseDeepLink();
                  noteLeavingStreamingTurn('new');
                  createNewSession('new_chat');
                }}
                {...PANEL_HEADER_BUTTON}
                isDisabled={isUploadingTurn}
              />
            </Tooltip>
            {!hideCollapseButton && (
              <PanelCollapseButton label="Collapse chat" onClick={onToggle} />
            )}
          </HStack>
        ) : (
          <Tooltip label="Open chat" placement="right">
            <IconButton
              aria-label="Open chat"
              icon={<MaterialIcon name="neurology" />}
              onClick={onToggle}
              variant="ghost"
              size="md"
              w="full"
              color="text.primary"
            />
          </Tooltip>
        )}
      </Flex>

      {/* Chat Content */}
      {isOpen && (
        <Flex flex={1} direction="column" overflow="hidden">
          {/*
            A failed history read, ABOVE the message list rather than in place
            of it. Everything already on screen — an in-flight turn, an earlier
            transcript — is still the truth, and this sits outside the scroll
            container so it stays visible on a transcript scrolled to the
            bottom, which is where the panel parks itself.
          */}
          {historyState.kind === 'load_failed' && (
            <Box px={4} pt={3}>
              <StatusBanner
                tone="warning"
                {...historyFailureCopy(messages.length > 0)}
                action={{
                  label: 'Try again',
                  onClick: handleRetryHistory,
                  // Otherwise the click does nothing observable — `isLoadingHistory`
                  // stays false throughout a refetch — and a second one aborts the
                  // first, because `refetch()` defaults to `cancelRefetch: true`.
                  isLoading: historyState.isRetrying,
                }}
              />
            </Box>
          )}
          <StoppedWaitingBanner turnStillWorking={turnStillWorking} />
          <RefusalAnnouncer messages={messages} />
          {/* Messages Area */}
          {/*
            `data-testid` so a spec can scope to the ANSWER rather than the
            page. The tier-1 T1-C journey asserts that every entity link Bower
            renders resolves to a real entity; the sidebar hierarchy renders
            `/notes/...` anchors of its own, so a page-wide sweep would grade
            the navigation instead of the reply.
          */}
          <Box
            ref={messagesContainerRef}
            data-testid="bird-messages"
            flex={1}
            overflowY="auto"
            px={4}
            py={3}
          >
            {messagesContent}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area — the shared Bower composer */}
          <Box px={4} pb={3} pt={2}>
            <ChatComposer
              onSubmit={handleComposerSubmit}
              {...composerTurnState(isStreaming, turnStillWorking)}
              onCancel={cancelMessage}
              onLiveTap={onLiveTap}
              prefill={composerPrefill}
              autoContextResetKey={sessionId ?? undefined}
              draftKey="panel"
              autoFocusOnMount
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default BirdChatPanel;
